import React, { useState, useRef, useEffect } from 'react';
import { X, Send, Loader2, Sparkles, Megaphone, Calendar, CheckSquare, Trash2 } from 'lucide-react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { aiAPI, announcementAPI, scheduleAPI, taskAPI, staffAPI } from '../lib/api';

const EVA_ROLES    = ['owner', 'manager'];
const CMD_ROLES    = ['owner', 'manager', 'super_admin'];
const MAX_MSG_LEN  = 500;

// ── Announcement parser ────────────────────────────────────────────────────────
function parseAnnouncement(text) {
  const lower = text.trimStart().toLowerCase();
  const prefix = ['announce:', 'post:'].find(p => lower.startsWith(p));
  if (!prefix) return null;
  const body = text.slice(lower.indexOf(prefix) + prefix.length).trim();
  return body; // '' = command with no body, null = not a command
}

function deriveTitle(body) {
  return body.split(/\s+/).slice(0, 7).join(' ').slice(0, 80);
}

// ── Natural-language pre-parser ───────────────────────────────────────────────
// Detects intent from plain text before parseEvaCommand runs.
function detectIntent(input) {
  const text = input.toLowerCase();
  if (/cancel.*shift/.test(text))              return 'cancel_shift';
  if (/schedule|add shift|create shift/.test(text)) return 'shift';
  if (/delete.*task/.test(text))               return 'delete_task';
  if (/task|todo/.test(text))                  return 'task';
  if (/announce|message|notify/.test(text))    return 'announce';
  return null;
}

function cleanInput(input, intent) {
  let text = input.trim();
  const patterns = {
    shift:        /^(schedule|add\s+shift|create\s+shift)\s+/i,
    cancel_shift: /^(cancel\s+shift\s*)/i,
    delete_task:  /^(delete\s+task\s*)/i,
    task:         /^(create\s+(a\s+)?task|add\s+(a\s+)?task|make\s+(a\s+)?task|task\s*)/i,
    announce:     /^(announce|message|notify)\s+/i,
  };
  if (patterns[intent]) text = text.replace(patterns[intent], '');
  return text.trim();
}

function convertToCommand(input, intent) {
  const cleaned = cleanInput(input, intent);
  if (intent === 'shift')        return `shift: ${cleaned}`;
  if (intent === 'task')         return `task: ${cleaned}`;
  if (intent === 'announce')     return `announce: ${cleaned}`;
  if (intent === 'cancel_shift') return `cancel shift: ${cleaned}`;
  if (intent === 'delete_task')  return `delete task: ${cleaned}`;
  return input;
}

// ── Employee name helper ───────────────────────────────────────────────────────
function empName(s) {
  return s.name || s.full_name || s.employee_name || '';
}

function similarity(a, b) {
  a = a.toLowerCase();
  b = b.toLowerCase();
  let matches = 0;
  for (let i = 0; i < Math.min(a.length, b.length); i++) {
    if (a[i] === b[i]) matches++;
  }
  return matches / Math.max(a.length, b.length);
}

function suggestNames(inputName, employees) {
  return employees
    .map(s => ({ name: empName(s), score: similarity(inputName, empName(s)) }))
    .filter(x => x.score > 0.25)
    .sort((a, b) => b.score - a.score)
    .slice(0, 3)
    .map(x => x.name);
}

const MAX_HOURS_PER_DAY = 12;

function isOverlap(aStart, aEnd, bStart, bEnd) {
  return aStart < bEnd && aEnd > bStart;
}

function timeToMinutes(timeStr) {
  if (!timeStr) return 0;
  const [h, m] = timeStr.split(':').map(Number);
  return h * 60 + (m || 0);
}

function findBestEmployeeMatch(inputName, employees) {
  const name = inputName.toLowerCase();

  const exact = employees.find(e => empName(e).toLowerCase() === name);
  if (exact) return { type: 'exact', match: exact };

  const starts = employees.find(e => empName(e).toLowerCase().startsWith(name));
  if (starts) return { type: 'starts', match: starts };

  const includes = employees.filter(e => empName(e).toLowerCase().includes(name));
  if (includes.length === 1) return { type: 'includes', match: includes[0] };
  if (includes.length > 1)  return { type: 'multiple', matches: includes };

  return { type: 'none' };
}

function normalizeTimeRange(text) {
  const t = text.toLowerCase();
  if (t.includes('morning'))   return text.replace(/morning/i,   '9am-1pm');
  if (t.includes('afternoon')) return text.replace(/afternoon/i, '1pm-5pm');
  if (t.includes('evening'))   return text.replace(/evening/i,   '5pm-9pm');
  if (t.includes('night'))     return text.replace(/night/i,     '9pm-12am');
  return text;
}

function normalizeTimeInput(text) {
  const match = text.match(/(\d{1,2})-(\d{1,2})/);
  if (!match) return text;

  const start = parseInt(match[1], 10);
  const end   = parseInt(match[2], 10);

  const startStr = parseTime(start.toString());
  let endStr;

  // end ≤ start → end is PM (e.g. 9-5 → 9am-5pm)
  if (end <= start) {
    endStr = end < 12 ? `${end}pm` : `${end}:00`;
  } else {
    endStr = parseTime(end.toString());
  }

  return text.replace(match[0], `${startStr}-${endStr}`);
}

// ── Shift / Task parser (exact spec) ─────────────────────────────────────────
function parseEvaCommand(input) {
  const raw = input.trim();
  const text = raw.toLowerCase();

  if (text.startsWith('shift:')) {
    const content = raw.replace(/^shift:/i, '').trim();
    const match = content.match(
      /(.+?)\s+(today|tomorrow|monday|tuesday|wednesday|thursday|friday|saturday|sunday)\s+(.+?)-(.+)/i
    );
    if (!match) return { error: 'Invalid shift format. Example:\nshift: John tomorrow 9am-5pm' };
    return { type: 'shift', name: match[1].trim(), day: match[2].toLowerCase(), start: match[3].trim(), end: match[4].trim() };
  }

  if (text.startsWith('task:')) {
    const content = raw.replace(/^task:/i, '').trim();
    if (!content) return { error: 'Task cannot be empty. Example:\ntask: Clean kitchen before opening' };
    return { type: 'task', text: content };
  }

  if (text.startsWith('cancel shift:')) {
    const content = raw.replace(/^cancel shift:/i, '').trim();
    const match = content.match(
      /(.+?)\s+(today|tomorrow|monday|tuesday|wednesday|thursday|friday|saturday|sunday)/i
    );
    if (!match) return { error: 'Invalid format. Example:\ncancel shift: John tomorrow' };
    return { type: 'cancel_shift', name: match[1].trim(), day: match[2].toLowerCase() };
  }

  if (text.startsWith('delete task:')) {
    const content = raw.replace(/^delete task:/i, '').trim();
    if (!content) return { error: 'Example: delete task: Clean kitchen' };
    return { type: 'delete_task', title: content };
  }

  return null;
}

// ── Date / time helpers ───────────────────────────────────────────────────────
function normalizeDay(day) {
  const map = {
    mon: 'monday', tue: 'tuesday', tues: 'tuesday',
    wed: 'wednesday', thu: 'thursday', thur: 'thursday', thurs: 'thursday',
    fri: 'friday', sat: 'saturday', sun: 'sunday',
  };
  const d = day.toLowerCase();
  return map[d] || d;
}

function resolveDate(day) {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  if (day === 'today') return today;
  if (day === 'tomorrow') { const d = new Date(today); d.setDate(d.getDate() + 1); return d; }
  const DAYS = ['sunday','monday','tuesday','wednesday','thursday','friday','saturday'];
  const target = DAYS.indexOf(day);
  if (target === -1) return null;
  const diff = ((target - today.getDay()) + 7) % 7 || 7; // next occurrence, never 0
  const d = new Date(today);
  d.setDate(d.getDate() + diff);
  return d;
}

function formatDateYMD(date) {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

function parseTime(timeStr) {
  let t = (timeStr || '').toLowerCase().trim();

  // bare integer → am/pm  e.g. 9 → 9am, 14 → 14:00
  if (/^\d{1,2}$/.test(t)) {
    const h = parseInt(t, 10);
    return h > 12 ? `${h}:00` : `${h}am`;
  }

  // already has am/pm → pass through
  if (t.includes('am') || t.includes('pm')) return t;

  // HH:MM → pass through
  if (/^\d{1,2}:\d{2}$/.test(t)) return t;

  return t;
}

function resolveTime(t) {
  const s = t.trim().toLowerCase().replace(/\s+/g, '');
  const m12 = s.match(/^(\d{1,2})(?::(\d{2}))?(am|pm)$/);
  if (m12) {
    let h = parseInt(m12[1]), min = parseInt(m12[2] || '0'), per = m12[3];
    if (per === 'pm' && h !== 12) h += 12;
    if (per === 'am' && h === 12) h = 0;
    return `${String(h).padStart(2, '0')}:${String(min).padStart(2, '0')}`;
  }
  const m24 = s.match(/^(\d{1,2}):(\d{2})$/);
  if (m24) return `${String(parseInt(m24[1])).padStart(2, '0')}:${m24[2]}`;
  return null;
}

// ── Quick actions ─────────────────────────────────────────────────────────────
const QUICK_ACTIONS = [
  { label: 'What is my labor cost this week?', type: 'send' },
  { label: 'Who is at overtime risk?',          type: 'send' },
  { label: 'Post announcement',                  type: 'fill', fillText: 'announce: ',      Icon: Megaphone   },
  { label: 'Add shift',                          type: 'fill', fillText: 'shift: ',         Icon: Calendar    },
  { label: 'Cancel shift',                       type: 'fill', fillText: 'cancel shift: ',  Icon: Trash2      },
  { label: 'Create task',                        type: 'fill', fillText: 'task: ',          Icon: CheckSquare },
  { label: 'Delete task',                        type: 'fill', fillText: 'delete task: ',   Icon: Trash2      },
];

export function EvaChat() {
  const { user } = useAuth();

  // Step 3: user-specific storage key
  const userId     = user?.id || user?._id || 'guest';
  const storageKey = `eva_chat_${userId}`;

  // Step 4: load messages from localStorage on init
  const [messages, setMessages] = useState(() => {
    try {
      const saved = localStorage.getItem(`eva_chat_${user?.id || user?._id || 'guest'}`);
      return saved ? JSON.parse(saved) : [
        {
          role: 'eva',
          content: "Hey — I'm Eva, your operations advisor.\n\nAsk me anything about your team, costs, or schedule — or use the quick actions below to add shifts, create tasks, and post announcements.",
        }
      ];
    } catch (e) {
      console.error('Failed to load chat:', e);
      return [
        {
          role: 'eva',
          content: "Hey — I'm Eva, your operations advisor.\n\nAsk me anything about your team, costs, or schedule — or use the quick actions below to add shifts, create tasks, and post announcements.",
        }
      ];
    }
  });
  const [open, setOpen] = useState(false);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [pendingCommand, setPendingCommand] = useState(null); // { type: 'announce'|'shift'|'task', ...data }
  const [pendingCancelShift, setPendingCancelShift] = useState(null); // { scheduleId, displayName, displayDate, displayTime }
  const [pendingDeleteTask, setPendingDeleteTask] = useState(null); // { taskId, title }
  const [pendingOverride, setPendingOverride] = useState(null); // { reason, warning, displayName, displayDate, displayTime, shiftPayload }
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  const canUseEva = EVA_ROLES.includes(user?.role);
  const navigate = useNavigate();

  // All hooks must be called before any conditional return
  useEffect(() => {
    if (open && messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages, open]);

  // Step 5: auto-save messages (capped at 50) on every change
  useEffect(() => {
    try {
      const trimmed = messages.slice(-50);
      localStorage.setItem(storageKey, JSON.stringify(trimmed));
    } catch (e) {
      console.error('Failed to save chat:', e);
    }
  }, [messages, storageKey]);

  // Step 6: reload correct chat when user switches
  useEffect(() => {
    try {
      const saved = localStorage.getItem(storageKey);
      setMessages(saved ? JSON.parse(saved) : [
        {
          role: 'eva',
          content: "Hey — I'm Eva, your operations advisor.\n\nAsk me anything about your team, costs, or schedule — or use the quick actions below to add shifts, create tasks, and post announcements.",
        }
      ]);
    } catch {
      setMessages([]);
    }
  }, [storageKey]);

  useEffect(() => {
    if (open && inputRef.current) {
      inputRef.current.focus();
    }
  }, [open]);

  // External trigger: any page can fire window.dispatchEvent(new CustomEvent('eva:open', { detail: responseText }))
  useEffect(() => {
    const handler = (e) => {
      const text = e.detail;
      if (!text) return;
      setMessages(prev => [...prev, { role: 'eva', content: text }]);
      setOpen(true);
    };
    window.addEventListener('eva:open', handler);
    return () => window.removeEventListener('eva:open', handler);
  }, []);

  if (!canUseEva) return null;

  const sendMessage = async (text) => {
    const msg = (text || input).trim();
    if (!msg || loading) return;

    if (msg.length > MAX_MSG_LEN) {
      setMessages(prev => [...prev, { role: 'error', content: `Message too long (${msg.length} chars). Max ${MAX_MSG_LEN}.` }]);
      return;
    }

    setMessages(prev => [...prev, { role: 'user', content: msg }]);
    setInput('');
    setLoading(true);

    try {
      // ── Pre-parser: natural language → command ────────────────────
      const intent = detectIntent(msg);
      let processedMessage = (intent && !msg.includes(':'))
        ? convertToCommand(msg, intent)
        : msg;

      // Normalize time words for shift commands
      if (intent === 'shift' || processedMessage.toLowerCase().startsWith('shift:')) {
        processedMessage = normalizeTimeRange(processedMessage);
        processedMessage = normalizeTimeInput(processedMessage);
      }

      // ── ANNOUNCE command ──────────────────────────────────────────
      const annBody = parseAnnouncement(processedMessage);
      if (annBody !== null) {
        if (!annBody) {
          setMessages(prev => [...prev, { role: 'eva', content: 'Please include a message after announce:\n\nExample:\nannounce: Team meeting today at 5pm in main office' }]);
          setLoading(false); return;
        }
        if (!CMD_ROLES.includes(user?.role)) {
          setMessages(prev => [...prev, { role: 'error', content: 'You do not have permission to post announcements.' }]);
          setLoading(false); return;
        }
        if (!user?.restaurant_id) {
          setMessages(prev => [...prev, { role: 'error', content: 'Unable to post: no location configured for your account.' }]);
          setLoading(false); return;
        }
        const title = deriveTitle(annBody);
        setMessages(prev => [...prev, { role: 'eva', content: "Here's a preview of your announcement. Confirm to post it to your team." }]);
        setPendingCommand({ type: 'announce', title, message: annBody });
        setLoading(false); return;
      }

      // ── SHIFT / TASK commands ─────────────────────────────────────
      const parsed = parseEvaCommand(processedMessage);

      if (parsed?.error) {
        setMessages(prev => [...prev, { role: 'eva', content: parsed.error }]);
        setLoading(false); return;
      }

      if (parsed?.type === 'shift') {
        if (!CMD_ROLES.includes(user?.role)) {
          setMessages(prev => [...prev, { role: 'error', content: 'You do not have permission to add shifts.' }]);
          setLoading(false); return;
        }
        // Resolve name → user_id
        let emp = null;
        try {
          const staffRes = await staffAPI.getAll(user.restaurant_id);
          const list = staffRes.data || [];
          const result = findBestEmployeeMatch(parsed.name, list);
          if (result.type === 'none') {
            const suggestions = suggestNames(parsed.name, list);
            if (suggestions.length > 0) {
              setMessages(prev => [...prev, {
                role: 'eva', type: 'suggestions',
                content: `No employee named "${parsed.name}" found. Did you mean:`,
                originalInput: processedMessage,
                wrongName: parsed.name,
                suggestions,
              }]);
            } else {
              setMessages(prev => [...prev, { role: 'eva', content: `No employee named "${parsed.name}" found. Check the name and try again.` }]);
            }
            setLoading(false); return;
          }
          if (result.type === 'multiple') {
            const names = result.matches.map(s => empName(s)).join(', ');
            setMessages(prev => [...prev, { role: 'eva', content: `Multiple employees match "${parsed.name}": ${names}.\n\nPlease be more specific.` }]);
            setLoading(false); return;
          }
          emp = result.match;
        } catch {
          setMessages(prev => [...prev, { role: 'error', content: 'Failed to load staff list. Please try again.' }]);
          setLoading(false); return;
        }
        const dateObj = resolveDate(normalizeDay(parsed.day));
        if (!dateObj) { setMessages(prev => [...prev, { role: 'eva', content: 'Invalid day. Use today, tomorrow, or a weekday name.' }]); setLoading(false); return; }
        const startTime = resolveTime(parseTime(parsed.start));
        const endTime   = resolveTime(parseTime(parsed.end));
        if (!startTime || !endTime) {
          setMessages(prev => [...prev, { role: 'eva', content: 'Invalid time format. Use 9am, 9:30am, or 17:00.\n\nExample: shift: John tomorrow 9am-5pm' }]);
          setLoading(false); return;
        }
        const displayDate = dateObj.toLocaleDateString('en-US', { weekday: 'long', month: 'short', day: 'numeric' });
        const displayName = empName(emp);
        const dateStr = formatDateYMD(dateObj);
        const empId = emp.user_id || emp.id;

        // ── Overlap + max-hours check ──────────────────────────────
        try {
          const schedRes = await scheduleAPI.getAll(user.restaurant_id, { start_date: dateStr, end_date: dateStr });
          const existing = (schedRes.data || []).filter(s =>
            (s.user_id === empId || s.employee_id === empId) && s.date === dateStr
          );
          const newStart = timeToMinutes(startTime);
          const newEnd   = timeToMinutes(endTime);

          // 1. Overlap
          const conflict = existing.find(s =>
            isOverlap(newStart, newEnd, timeToMinutes(s.start_time), timeToMinutes(s.end_time))
          );
          if (conflict) {
            const shiftPayload = { restaurantId: user.restaurant_id, userId: empId, date: dateStr, startTime, endTime };
            setMessages(prev => [...prev, { role: 'eva', content: `${displayName} already has a shift from ${conflict.start_time}–${conflict.end_time} on ${displayDate}. Confirm below to override.` }]);
            setPendingOverride({ reason: 'overlap', warning: `Overlaps with ${conflict.start_time}–${conflict.end_time}`, displayName, displayDate, displayTime: `${startTime} – ${endTime}`, shiftPayload });
            setLoading(false); return;
          }

          // 2. Max daily hours
          const existingMinutes = existing.reduce((sum, s) =>
            sum + Math.max(0, timeToMinutes(s.end_time) - timeToMinutes(s.start_time)), 0
          );
          const newMinutes   = Math.max(0, newEnd - newStart);
          const totalHours   = (existingMinutes + newMinutes) / 60;
          if (totalHours > MAX_HOURS_PER_DAY) {
            const shiftPayload = { restaurantId: user.restaurant_id, userId: empId, date: dateStr, startTime, endTime };
            setMessages(prev => [...prev, { role: 'eva', content: `This shift puts ${displayName} at ${totalHours.toFixed(1)}h on ${displayDate} (limit: ${MAX_HOURS_PER_DAY}h). Confirm below to override.` }]);
            setPendingOverride({ reason: 'max_hours', warning: `${totalHours.toFixed(1)}h exceeds the ${MAX_HOURS_PER_DAY}h daily limit`, displayName, displayDate, displayTime: `${startTime} – ${endTime}`, shiftPayload });
            setLoading(false); return;
          }
        } catch { /* non-blocking: skip checks on API failure */ }

        setMessages(prev => [...prev, { role: 'eva', content: "Here's a preview of the shift. Confirm to add it to the schedule." }]);
        setPendingCommand({
          type: 'shift', displayName, displayDate,
          displayTime: `${startTime} – ${endTime}`,
          userId: empId,
          date: dateStr, startTime, endTime,
        });
        setLoading(false); return;
      }

      if (parsed?.type === 'task') {
        if (!CMD_ROLES.includes(user?.role)) {
          setMessages(prev => [...prev, { role: 'error', content: 'You do not have permission to create tasks.' }]);
          setLoading(false); return;
        }
        setMessages(prev => [...prev, { role: 'eva', content: "Here's a preview of the task. Confirm to add it." }]);
        setPendingCommand({ type: 'task', title: parsed.text });
        setLoading(false); return;
      }

      if (parsed?.type === 'cancel_shift') {
        if (!CMD_ROLES.includes(user?.role)) {
          setMessages(prev => [...prev, { role: 'error', content: 'You do not have permission to cancel shifts.' }]);
          setLoading(false); return;
        }
        // Resolve employee name → object
        let emp = null;
        try {
          const staffRes = await staffAPI.getAll(user.restaurant_id);
          const list = staffRes.data || [];
          const result = findBestEmployeeMatch(parsed.name, list);
          if (result.type === 'none') {
            const suggestions = suggestNames(parsed.name, list);
            if (suggestions.length > 0) {
              setMessages(prev => [...prev, {
                role: 'eva', type: 'suggestions',
                content: `No employee named "${parsed.name}" found. Did you mean:`,
                originalInput: processedMessage,
                wrongName: parsed.name,
                suggestions,
              }]);
            } else {
              setMessages(prev => [...prev, { role: 'eva', content: `No employee named "${parsed.name}" found. Check the name and try again.` }]);
            }
            setLoading(false); return;
          }
          if (result.type === 'multiple') {
            const names = result.matches.map(s => empName(s)).join(', ');
            setMessages(prev => [...prev, { role: 'eva', content: `Multiple employees match "${parsed.name}": ${names}.\n\nPlease be more specific.` }]);
            setLoading(false); return;
          }
          emp = result.match;
        } catch {
          setMessages(prev => [...prev, { role: 'error', content: 'Failed to load staff list. Please try again.' }]);
          setLoading(false); return;
        }
        // Resolve day → date
        const dateObj = resolveDate(normalizeDay(parsed.day));
        if (!dateObj) {
          setMessages(prev => [...prev, { role: 'eva', content: 'Invalid day. Use today, tomorrow, or a weekday name.' }]);
          setLoading(false); return;
        }
        const dateStr = formatDateYMD(dateObj);
        const empId = emp.user_id || emp.id;
        // Find scheduled shifts for that employee on that day
        let matchingShifts = [];
        try {
          const schedRes = await scheduleAPI.getAll(user.restaurant_id, { start_date: dateStr, end_date: dateStr });
          const all = schedRes.data || [];
          matchingShifts = all.filter(s => (s.user_id === empId || s.employee_id === empId) && s.date === dateStr);
        } catch {
          setMessages(prev => [...prev, { role: 'error', content: 'Failed to load schedule. Please try again.' }]);
          setLoading(false); return;
        }
        const displayName = empName(emp);
        const displayDate = dateObj.toLocaleDateString('en-US', { weekday: 'long', month: 'short', day: 'numeric' });

        if (matchingShifts.length === 0) {
          setMessages(prev => [...prev, { role: 'eva', content: `No scheduled shift found for ${displayName} on ${displayDate}.` }]);
          setLoading(false); return;
        }

        // Multiple shifts on same day — list them and ask for time to resolve
        if (matchingShifts.length > 1) {
          const shiftList = matchingShifts
            .map((s, i) => `${i + 1}. ${s.start_time || '?'} – ${s.end_time || '?'}`)
            .join('\n');
          setMessages(prev => [...prev, {
            role: 'eva',
            content: `${displayName} has ${matchingShifts.length} shifts on ${displayDate}:\n\n${shiftList}\n\nSpecify the time to cancel — e.g. cancel shift: ${parsed.name} ${parsed.day} 9am`
          }]);
          setLoading(false); return;
        }

        const shift = matchingShifts[0];
        const displayTime = (shift.start_time && shift.end_time) ? `${shift.start_time} – ${shift.end_time}` : 'Time not specified';
        setMessages(prev => [...prev, { role: 'eva', content: 'Found the shift. Confirm to remove it from the schedule.' }]);
        setPendingCancelShift({
          scheduleId: shift.id || shift._id,
          displayName,
          displayDate,
          displayTime,
        });
        setLoading(false); return;
      }

      if (parsed?.type === 'delete_task') {
        if (!CMD_ROLES.includes(user?.role)) {
          setMessages(prev => [...prev, { role: 'error', content: 'You do not have permission to delete tasks.' }]);
          setLoading(false); return;
        }
        // Fetch all tasks for this location
        let tasks = [];
        try {
          const tasksRes = await taskAPI.getAll(user.restaurant_id);
          tasks = tasksRes.data || [];
        } catch {
          setMessages(prev => [...prev, { role: 'error', content: 'Failed to load tasks. Please try again.' }]);
          setLoading(false); return;
        }
        const q = parsed.title.toLowerCase();
        // Exact case-insensitive match first, then substring fallback
        let hits = tasks.filter(t => (t.title || '').toLowerCase() === q);
        if (hits.length === 0) hits = tasks.filter(t => (t.title || '').toLowerCase().includes(q));
        if (hits.length === 0) {
          setMessages(prev => [...prev, { role: 'eva', content: `No task found matching "${parsed.title}". Check the title and try again.` }]);
          setLoading(false); return;
        }
        if (hits.length > 1) {
          const titles = hits.map(t => `• ${t.title}`).join('\n');
          setMessages(prev => [...prev, { role: 'eva', content: `Multiple tasks match "${parsed.title}":\n\n${titles}\n\nPlease use the exact full title.` }]);
          setLoading(false); return;
        }
        const task = hits[0];
        setMessages(prev => [...prev, { role: 'eva', content: "Found the task. Confirm to delete it permanently." }]);
        setPendingDeleteTask({ taskId: task.id || task._id, title: task.title });
        setLoading(false); return;
      }

      // ── Normal AI chat ────────────────────────────────────────────
      const res = await aiAPI.chat(msg);
      setMessages(prev => [...prev, { role: 'eva', content: res.data.response }]);
    } catch (err) {
      const detail = err?.response?.data?.detail || 'Something went wrong. Please try again.';
      setMessages(prev => [...prev, { role: 'error', content: detail }]);
    } finally {
      setLoading(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const confirmCommand = async () => {
    if (!pendingCommand) return;
    const cmd = pendingCommand;
    setPendingCommand(null);
    setLoading(true);
    try {
      if (cmd.type === 'announce') {
        await announcementAPI.create(user.restaurant_id, { title: cmd.title, message: cmd.message });
        setMessages(prev => [...prev, { role: 'eva', content: `Announcement posted successfully.\n\nTitle: "${cmd.title}"\nMessage: "${cmd.message}"\n\nYour team has been notified.` }]);
      } else if (cmd.type === 'shift') {
        await scheduleAPI.create(user.restaurant_id, { user_id: cmd.userId, date: cmd.date, start_time: cmd.startTime, end_time: cmd.endTime, notes: 'Added via Eva' });
        setMessages(prev => [...prev, { role: 'eva', content: `Shift added to the schedule.\n\nEmployee: ${cmd.displayName}\nDate: ${cmd.displayDate}\nTime: ${cmd.displayTime}` }]);
      } else if (cmd.type === 'task') {
        await taskAPI.create(user.restaurant_id, { title: cmd.title, type: 'general' });
        setMessages(prev => [...prev, { role: 'eva', content: `Task created successfully.\n\nTitle: "${cmd.title}"` }]);
      }
    } catch (err) {
      const detail = err?.response?.data?.detail || 'Something went wrong. Please try again.';
      setMessages(prev => [...prev, { role: 'error', content: detail }]);
    } finally {
      setLoading(false);
    }
  };

  const cancelCommand = () => {
    const t = pendingCommand?.type;
    setPendingCommand(null);
    const msg = { announce: 'Announcement cancelled. Nothing was posted.', shift: 'Shift not added. Nothing was changed.', task: 'Task cancelled. Nothing was created.' };
    setMessages(prev => [...prev, { role: 'eva', content: msg[t] || 'Action cancelled.' }]);
  };

  const confirmCancelShift = async () => {
    if (!pendingCancelShift) return;
    const cmd = pendingCancelShift;
    setPendingCancelShift(null);
    setLoading(true);
    try {
      await scheduleAPI.delete(cmd.scheduleId);
      setMessages(prev => [...prev, { role: 'eva', content: `Shift removed from the schedule.\n\nEmployee: ${cmd.displayName}\nDate: ${cmd.displayDate}\nTime: ${cmd.displayTime}` }]);
    } catch (err) {
      const detail = err?.response?.data?.detail || 'Failed to remove shift. Please try again.';
      setMessages(prev => [...prev, { role: 'error', content: detail }]);
    } finally {
      setLoading(false);
    }
  };

  const cancelCancelShift = () => {
    setPendingCancelShift(null);
    setMessages(prev => [...prev, { role: 'eva', content: 'Shift removal cancelled. No changes made.' }]);
  };

  const confirmDeleteTask = async () => {
    if (!pendingDeleteTask) return;
    const cmd = pendingDeleteTask;
    setPendingDeleteTask(null);
    setLoading(true);
    try {
      await taskAPI.delete(cmd.taskId);
      setMessages(prev => [...prev, { role: 'eva', content: `Task deleted.\n\n"${cmd.title}" has been permanently removed.` }]);
    } catch (err) {
      const detail = err?.response?.data?.detail || 'Failed to delete task. Please try again.';
      setMessages(prev => [...prev, { role: 'error', content: detail }]);
    } finally {
      setLoading(false);
    }
  };

  const cancelDeleteTask = () => {
    setPendingDeleteTask(null);
    setMessages(prev => [...prev, { role: 'eva', content: 'Task deletion cancelled. Nothing was changed.' }]);
  };

  const confirmOverride = async () => {
    if (!pendingOverride) return;
    const { shiftPayload, displayName, displayDate, displayTime } = pendingOverride;
    setPendingOverride(null);
    setLoading(true);
    try {
      await scheduleAPI.create(shiftPayload.restaurantId, {
        user_id: shiftPayload.userId,
        date: shiftPayload.date,
        start_time: shiftPayload.startTime,
        end_time: shiftPayload.endTime,
        notes: 'Added via Eva (manager override)',
      });
      setMessages(prev => [...prev, { role: 'eva', content: `Shift created (override).\n\nEmployee: ${displayName}\nDate: ${displayDate}\nTime: ${displayTime}` }]);
    } catch (err) {
      const detail = err?.response?.data?.detail || 'Failed to create shift.';
      setMessages(prev => [...prev, { role: 'error', content: detail }]);
    } finally {
      setLoading(false);
    }
  };

  const cancelOverride = () => {
    setPendingOverride(null);
    setMessages(prev => [...prev, { role: 'eva', content: 'Override cancelled. No shift was created.' }]);
  };

  const retrySuggestion = (originalInput, wrongName, suggestion) => {
    // Replace the wrong name with the chosen suggestion (case-insensitive, first occurrence)
    const corrected = originalInput.replace(new RegExp(wrongName.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'), 'i'), suggestion);
    sendMessage(corrected);
  };

  return (
    <>
      {/* ── Floating Launcher Button ── */}
      {!open && (
        <button
          onClick={() => setOpen(true)}
          data-testid="eva-launcher-btn"
          className="eva-launcher fixed z-50 flex items-center gap-2 bg-orange-500 hover:bg-orange-600 text-white font-semibold shadow-lg rounded-full transition-all duration-200 hover:scale-105 active:scale-95"
          style={{ padding: '12px 18px' }}
          aria-label="Open Eva AI assistant"
        >
          <Sparkles size={16} className="flex-shrink-0" />
          <span className="eva-launcher-label">Ask Eva</span>
        </button>
      )}

      {/* ── Chat Panel ── */}
      {open && (
        <>
          {/* Mobile backdrop */}
          <div
            className="eva-mobile-backdrop fixed inset-0 bg-black/30 z-40"
            onClick={() => setOpen(false)}
            data-testid="eva-backdrop"
          />

          <div
            className="eva-panel fixed z-50 bg-white rounded-2xl shadow-2xl flex flex-col overflow-hidden"
            data-testid="eva-chat-panel"
            style={{ border: '1px solid #e2e8f0' }}
          >
            {/* Header */}
            <div className="flex items-center justify-between px-4 py-3 bg-orange-500 flex-shrink-0">
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 bg-white/20 rounded-full flex items-center justify-center">
                  <Sparkles size={16} className="text-white" />
                </div>
                <div>
                  <p className="text-white font-bold text-sm leading-tight">Eva</p>
                  <p className="text-orange-100 text-xs leading-tight">How can I help you today?</p>
                </div>
              </div>
              <div className="flex items-center gap-1">
                <button
                  onClick={() => {
                    localStorage.removeItem(storageKey);
                    setMessages([{
                      role: 'eva',
                      content: "Hey — I'm Eva, your operations advisor.\n\nAsk me anything about your team, costs, or schedule — or use the quick actions below to add shifts, create tasks, and post announcements.",
                    }]);
                  }}
                  className="p-1.5 text-white/60 hover:text-white hover:bg-white/20 rounded-lg transition-colors text-xs font-medium"
                  title="Clear chat"
                  data-testid="eva-clear-chat-btn"
                >
                  Clear
                </button>
                <Link
                  to="/help/eva-ai"
                  className="p-1.5 text-white/70 hover:text-white hover:bg-white/20 rounded-lg transition-colors text-sm font-medium"
                  title="Eva Help Guide"
                  data-testid="eva-help-link"
                >
                  ?
                </Link>
                <button
                  onClick={() => setOpen(false)}
                  className="p-1.5 text-white/80 hover:text-white hover:bg-white/20 rounded-lg transition-colors"
                  data-testid="eva-close-btn"
                >
                  <X size={18} />
                </button>
              </div>
            </div>

            {/* Messages */}
            <div className="flex-1 overflow-y-auto p-3 space-y-3 min-h-0">
              {messages.length <= 1 && (
                <div className="py-2">
                  {/* Quick actions */}
                  <p className="text-xs text-slate-400 font-medium mb-2 px-1">Quick actions</p>
                  <div className="flex flex-wrap gap-2">
                    {QUICK_ACTIONS.map(({ label, type, fillText, Icon: QIcon }) => (
                      <button
                        key={label}
                        onClick={() => {
                          if (type === 'fill') { setInput(fillText); inputRef.current?.focus(); }
                          else { sendMessage(label); }
                        }}
                        className="text-xs px-3 py-1.5 rounded-full border border-slate-200 text-slate-600 hover:border-orange-300 hover:text-orange-600 hover:bg-orange-50 transition-colors flex items-center gap-1"
                        data-testid={`eva-quick-${label.toLowerCase().replace(/\s+/g, '-')}`}
                      >
                        {QIcon && <QIcon size={10} className="opacity-60" />}
                        {label}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {messages.map((msg, i) => (
                <div
                  key={i}
                  className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
                >
                  {/* Suggestion bubble — tappable name buttons */}
                  {msg.type === 'suggestions' ? (
                    <div className="max-w-[85%] bg-slate-100 text-slate-800 rounded-2xl rounded-bl-sm px-3 py-2.5" data-testid="eva-suggestion-bubble">
                      <p className="text-sm mb-2">{msg.content}</p>
                      <div className="flex flex-wrap gap-1.5">
                        {msg.suggestions.map(name => (
                          <button
                            key={name}
                            onClick={() => retrySuggestion(msg.originalInput, msg.wrongName, name)}
                            data-testid={`eva-suggestion-btn-${name.toLowerCase().replace(/\s+/g, '-')}`}
                            className="px-3 py-1 bg-white border border-orange-300 text-orange-700 rounded-full text-xs font-medium hover:bg-orange-50 hover:border-orange-400 transition-colors"
                          >
                            {name}
                          </button>
                        ))}
                      </div>
                    </div>
                  ) : (
                  <div
                    className={`max-w-[85%] text-sm px-3 py-2 rounded-2xl whitespace-pre-wrap break-words ${
                      msg.role === 'user'
                        ? 'bg-orange-500 text-white rounded-br-sm'
                        : msg.role === 'error'
                        ? 'bg-red-50 text-red-600 border border-red-100 rounded-bl-sm'
                        : 'bg-slate-100 text-slate-800 rounded-bl-sm'
                    }`}
                  >
                    {msg.role === 'error' && msg.content.includes('AI not configured') ? (
                      <span>
                        AI not configured. Add your Gemini API key in{' '}
                        <span
                          className="underline cursor-pointer font-medium"
                          onClick={() => navigate('/settings')}
                          data-testid="eva-settings-link"
                        >
                          Settings
                        </span>
                        .
                      </span>
                    ) : (
                      msg.content
                    )}
                  </div>
                  )}
                </div>
              ))}

              {loading && (
                <div className="flex justify-start">
                  <div className="bg-slate-100 rounded-2xl rounded-bl-sm px-3 py-2">
                    <Loader2 size={16} className="text-orange-500 animate-spin" />
                  </div>
                </div>
              )}
              <div ref={messagesEndRef} />
            </div>

            {/* ── Command preview card ── */}
            {pendingCommand && (() => {
              const t = pendingCommand.type;
              const colors = {
                announce: { border: 'border-orange-200', bg: 'bg-orange-50', label: 'text-orange-700', btn: 'bg-orange-500 hover:bg-orange-600', Icon: Megaphone, iconCls: 'text-orange-500' },
                shift:    { border: 'border-teal-200',   bg: 'bg-teal-50',   label: 'text-teal-700',   btn: 'bg-teal-500 hover:bg-teal-600',   Icon: Calendar,   iconCls: 'text-teal-500'   },
                task:     { border: 'border-violet-200', bg: 'bg-violet-50', label: 'text-violet-700', btn: 'bg-violet-500 hover:bg-violet-600', Icon: CheckSquare, iconCls: 'text-violet-500' },
              };
              const c = colors[t] || colors.announce;
              const typeLabel = { announce: 'Announcement', shift: 'Shift', task: 'Task' }[t];
              const btnLabel  = { announce: 'Confirm Post', shift: 'Add Shift', task: 'Add Task' }[t];
              return (
                <div
                  className={`flex-shrink-0 mx-3 mb-2 rounded-2xl border p-3 ${c.border} ${c.bg}`}
                  data-testid={`eva-${t}-preview`}
                >
                  <div className="flex items-center gap-1.5 mb-2">
                    <c.Icon size={13} className={`${c.iconCls} flex-shrink-0`} />
                    <p className={`text-xs font-bold uppercase tracking-wide ${c.label}`}>Preview — {typeLabel}</p>
                  </div>

                  {t === 'announce' && (
                    <>
                      <p className="text-sm font-semibold text-slate-900 mb-0.5 break-words" data-testid="eva-announce-preview-title">{pendingCommand.title}</p>
                      <p className="text-sm text-slate-600 mb-3 leading-relaxed break-words" data-testid="eva-announce-preview-message">{pendingCommand.message}</p>
                    </>
                  )}
                  {t === 'shift' && (
                    <div className="mb-3 space-y-0.5">
                      <p className="text-sm font-semibold text-slate-900" data-testid="eva-shift-preview-name">{pendingCommand.displayName}</p>
                      <p className="text-sm text-slate-600" data-testid="eva-shift-preview-date">{pendingCommand.displayDate}</p>
                      <p className="text-sm text-slate-600 font-medium" data-testid="eva-shift-preview-time">{pendingCommand.displayTime}</p>
                    </div>
                  )}
                  {t === 'task' && (
                    <div className="mb-3">
                      <p className="text-sm font-semibold text-slate-900 break-words" data-testid="eva-task-preview-title">{pendingCommand.title}</p>
                      <p className="text-xs text-slate-500 mt-0.5">Type: General</p>
                    </div>
                  )}

                  <div className="flex gap-2">
                    <button onClick={confirmCommand} disabled={loading} data-testid={`eva-${t}-confirm-btn`}
                      className={`flex-1 py-2 text-white text-sm rounded-xl font-semibold disabled:opacity-50 transition-colors ${c.btn}`}>
                      {btnLabel}
                    </button>
                    <button onClick={cancelCommand} disabled={loading} data-testid={`eva-${t}-cancel-btn`}
                      className="flex-1 py-2 bg-slate-200 hover:bg-slate-300 disabled:opacity-50 text-slate-700 text-sm rounded-xl font-semibold transition-colors">
                      Cancel
                    </button>
                  </div>
                </div>
              );
            })()}

            {/* ── Cancel Shift preview card ── */}
            {pendingCancelShift && (
              <div
                className="flex-shrink-0 mx-3 mb-2 rounded-2xl border border-red-200 bg-red-50 p-3"
                data-testid="eva-cancel_shift-preview"
              >
                <div className="flex items-center gap-1.5 mb-2">
                  <Trash2 size={13} className="text-red-500 flex-shrink-0" />
                  <p className="text-xs font-bold uppercase tracking-wide text-red-700">Preview — Cancel Shift</p>
                </div>
                <div className="mb-3 space-y-0.5">
                  <p className="text-sm font-semibold text-slate-900" data-testid="eva-cancel-shift-preview-name">{pendingCancelShift.displayName}</p>
                  <p className="text-sm text-slate-600" data-testid="eva-cancel-shift-preview-date">{pendingCancelShift.displayDate}</p>
                  <p className="text-sm text-slate-600 font-medium" data-testid="eva-cancel-shift-preview-time">{pendingCancelShift.displayTime}</p>
                  <p className="text-xs text-red-500 mt-1">This shift will be permanently removed.</p>
                </div>
                <div className="flex gap-2">
                  <button onClick={confirmCancelShift} disabled={loading} data-testid="eva-cancel_shift-confirm-btn"
                    className="flex-1 py-2 text-white text-sm rounded-xl font-semibold disabled:opacity-50 transition-colors bg-red-500 hover:bg-red-600">
                    Remove Shift
                  </button>
                  <button onClick={cancelCancelShift} disabled={loading} data-testid="eva-cancel_shift-cancel-btn"
                    className="flex-1 py-2 bg-slate-200 hover:bg-slate-300 disabled:opacity-50 text-slate-700 text-sm rounded-xl font-semibold transition-colors">
                    Keep It
                  </button>
                </div>
              </div>
            )}

            {/* ── Delete Task preview card ── */}
            {pendingDeleteTask && (
              <div
                className="flex-shrink-0 mx-3 mb-2 rounded-2xl border border-red-200 bg-red-50 p-3"
                data-testid="eva-delete_task-preview"
              >
                <div className="flex items-center gap-1.5 mb-2">
                  <Trash2 size={13} className="text-red-500 flex-shrink-0" />
                  <p className="text-xs font-bold uppercase tracking-wide text-red-700">Preview — Delete Task</p>
                </div>
                <div className="mb-3">
                  <p className="text-sm font-semibold text-slate-900 break-words" data-testid="eva-delete-task-preview-title">{pendingDeleteTask.title}</p>
                  <p className="text-xs text-red-500 mt-1">This task will be permanently deleted.</p>
                </div>
                <div className="flex gap-2">
                  <button onClick={confirmDeleteTask} disabled={loading} data-testid="eva-delete_task-confirm-btn"
                    className="flex-1 py-2 text-white text-sm rounded-xl font-semibold disabled:opacity-50 transition-colors bg-red-500 hover:bg-red-600">
                    Delete Task
                  </button>
                  <button onClick={cancelDeleteTask} disabled={loading} data-testid="eva-delete_task-cancel-btn"
                    className="flex-1 py-2 bg-slate-200 hover:bg-slate-300 disabled:opacity-50 text-slate-700 text-sm rounded-xl font-semibold transition-colors">
                    Keep It
                  </button>
                </div>
              </div>
            )}

            {/* ── Override warning card ── */}
            {pendingOverride && (
              <div
                className="flex-shrink-0 mx-3 mb-2 rounded-2xl border border-amber-200 bg-amber-50 p-3"
                data-testid="eva-override-preview"
              >
                <div className="flex items-center gap-1.5 mb-2">
                  <Trash2 size={13} className="text-amber-500 flex-shrink-0" />
                  <p className="text-xs font-bold uppercase tracking-wide text-amber-700">
                    {pendingOverride.reason === 'overlap' ? 'Warning — Shift Overlap' : 'Warning — Daily Hours Exceeded'}
                  </p>
                </div>
                <div className="mb-3 space-y-0.5">
                  <p className="text-sm font-semibold text-slate-900">{pendingOverride.displayName}</p>
                  <p className="text-sm text-slate-600">{pendingOverride.displayDate} · {pendingOverride.displayTime}</p>
                  <p className="text-xs text-amber-600 mt-1">{pendingOverride.warning}</p>
                </div>
                <div className="flex gap-2">
                  <button onClick={confirmOverride} disabled={loading} data-testid="eva-override-confirm-btn"
                    className="flex-1 py-2 text-white text-sm rounded-xl font-semibold disabled:opacity-50 transition-colors bg-amber-500 hover:bg-amber-600">
                    Force Schedule
                  </button>
                  <button onClick={cancelOverride} disabled={loading} data-testid="eva-override-cancel-btn"
                    className="flex-1 py-2 bg-slate-200 hover:bg-slate-300 disabled:opacity-50 text-slate-700 text-sm rounded-xl font-semibold transition-colors">
                    Cancel
                  </button>
                </div>
              </div>
            )}

            {/* Input */}
            <div className="flex-shrink-0 px-3 pb-3">
              {/* Quick-action pills */}
              <div className="flex flex-wrap gap-1.5 mb-2">
                <button onClick={() => { setInput('announce: '); inputRef.current?.focus(); }} data-testid="eva-pill-announce"
                  className="px-3 py-1 bg-orange-100 text-orange-600 rounded-full text-xs font-medium hover:bg-orange-200 transition-colors">
                  Announcement
                </button>
                <button onClick={() => { setInput('shift: '); inputRef.current?.focus(); }} data-testid="eva-pill-shift"
                  className="px-3 py-1 bg-blue-100 text-blue-600 rounded-full text-xs font-medium hover:bg-blue-200 transition-colors">
                  Add Shift
                </button>
                <button onClick={() => { setInput('cancel shift: '); inputRef.current?.focus(); }} data-testid="eva-pill-cancel-shift"
                  className="px-3 py-1 bg-red-100 text-red-600 rounded-full text-xs font-medium hover:bg-red-200 transition-colors">
                  Cancel Shift
                </button>
                <button onClick={() => { setInput('task: '); inputRef.current?.focus(); }} data-testid="eva-pill-task"
                  className="px-3 py-1 bg-purple-100 text-purple-600 rounded-full text-xs font-medium hover:bg-purple-200 transition-colors">
                  Task
                </button>
                <button onClick={() => { setInput('delete task: '); inputRef.current?.focus(); }} data-testid="eva-pill-delete-task"
                  className="px-3 py-1 bg-slate-200 text-slate-600 rounded-full text-xs font-medium hover:bg-slate-300 transition-colors">
                  Delete Task
                </button>
              </div>
              <div className="flex items-end gap-2 rounded-xl border border-slate-200 bg-slate-50 px-3 py-2">
                <textarea
                  ref={inputRef}
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  onKeyDown={handleKeyDown}
                  rows={1}
                  placeholder="Ask Eva anything..."
                  disabled={loading}
                  data-testid="eva-input"
                  className="flex-1 bg-transparent text-sm text-slate-800 placeholder-slate-400 resize-none outline-none min-h-[20px] max-h-[80px]"
                  style={{ lineHeight: '20px' }}
                />
                <button
                  onClick={() => sendMessage()}
                  disabled={!input.trim() || loading}
                  data-testid="eva-send-btn"
                  className="flex-shrink-0 w-8 h-8 rounded-lg bg-orange-500 hover:bg-orange-600 disabled:opacity-40 disabled:cursor-not-allowed text-white flex items-center justify-center transition-colors"
                >
                  <Send size={14} />
                </button>
              </div>
            </div>
          </div>
        </>
      )}

      {/* ── Responsive styles ── */}
      <style>{`
        /* Desktop: bottom-right */
        .eva-launcher {
          bottom: 24px;
          right: 24px;
        }
        .eva-launcher .eva-launcher-label {
          display: inline;
        }
        .eva-panel {
          bottom: 88px;
          right: 24px;
          width: 380px;
          height: 520px;
        }
        .eva-mobile-backdrop {
          display: none;
        }

        /* Mobile: bottom-right, above the bottom nav */
        @media (max-width: 767px) {
          .eva-launcher {
            bottom: 76px;
            right: 16px;
            left: auto;
            padding: 10px 14px;
          }
          .eva-launcher .eva-launcher-label {
            display: none;
          }
          .eva-panel {
            bottom: 0;
            right: 0;
            left: 0;
            width: 100%;
            height: 70vh;
            border-radius: 20px 20px 0 0;
          }
          .eva-mobile-backdrop {
            display: block;
          }
        }
      `}</style>
    </>
  );
}
